import { matches } from './utils';

/**
 * Type for field accessor function
 */
export type FieldAccessor<T> = (item: T) => string | null | undefined;

/**
 * Type for filter function
 */
export type FilterFunction<T> = (item: T, query: string) => boolean;

/**
 * Creates a filter function that searches across multiple fields
 * Returns true if ANY field matches the query (OR logic)
 *
 * @param accessors - Array of functions that extract field values from items
 * @returns Filter function that can be used with array.filter()
 *
 * @example
 * ```tsx
 * const filter = createMultiFieldFilter<Consignee>(
 *   c => c.code,
 *   c => c.name,
 *   c => c.port_id
 * );
 * // Usage: items.filter(item => filter(item, 'search query'))
 * ```
 */
export function createMultiFieldFilter<T>(
  ...accessors: FieldAccessor<T>[]
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    if (!query) return true;

    return accessors.some(accessor => {
      const value = accessor(item);
      return value ? matches(value, query) : false;
    });
  };
}

/**
 * Creates a filter function for a single field
 * Useful when you only need to search one field
 *
 * @param accessor - Function that extracts the field value from an item
 * @returns Filter function
 *
 * @example
 * ```tsx
 * const filter = createFieldFilter<Port>(p => p.code);
 * ```
 */
export function createFieldFilter<T>(
  accessor: FieldAccessor<T>
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    if (!query) return true;
    const value = accessor(item);
    return value ? matches(value, query) : false;
  };
}

/**
 * Creates a filter function with custom logic
 * Allows for complex filtering scenarios beyond simple text matching
 *
 * @param predicate - Custom predicate function
 * @returns Filter function
 *
 * @example
 * ```tsx
 * const filter = createCustomFilter<Product>((item, query) => {
 *   const price = parseFloat(query);
 *   return !isNaN(price) ? item.price <= price : matches(item.name, query);
 * });
 * ```
 */
export function createCustomFilter<T>(
  predicate: (item: T, query: string) => boolean
): FilterFunction<T> {
  return predicate;
}

/**
 * Combines multiple filter functions with AND logic
 * Returns true only if ALL filters match
 *
 * @param filters - Array of filter functions to combine
 * @returns Combined filter function
 *
 * @example
 * ```tsx
 * const categoryFilter = createFieldFilter<Product>(p => p.category);
 * const nameFilter = createFieldFilter<Product>(p => p.name);
 * const combinedFilter = combineFiltersAnd(categoryFilter, nameFilter);
 * ```
 */
export function combineFiltersAnd<T>(
  ...filters: FilterFunction<T>[]
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    return filters.every(filter => filter(item, query));
  };
}

/**
 * Combines multiple filter functions with OR logic
 * Returns true if ANY filter matches
 *
 * @param filters - Array of filter functions to combine
 * @returns Combined filter function
 */
export function combineFiltersOr<T>(
  ...filters: FilterFunction<T>[]
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    return filters.some(filter => filter(item, query));
  };
}

/**
 * Creates a case-sensitive filter
 * By default, all filters are case-insensitive via the `matches` utility
 *
 * @param accessor - Field accessor function
 * @returns Case-sensitive filter function
 */
export function createCaseSensitiveFilter<T>(
  accessor: FieldAccessor<T>
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    if (!query) return true;
    const value = accessor(item);
    return value ? value.includes(query) : false;
  };
}

/**
 * Creates a filter that matches items starting with the query
 * Useful for autocomplete scenarios
 *
 * @param accessor - Field accessor function
 * @returns Starts-with filter function
 */
export function createStartsWithFilter<T>(
  accessor: FieldAccessor<T>
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    if (!query) return true;
    const value = accessor(item);
    if (!value) return false;

    const normalizedValue = value.toLowerCase().trim();
    const normalizedQuery = query.toLowerCase().trim();
    return normalizedValue.startsWith(normalizedQuery);
  };
}

/**
 * Creates a filter that matches exact values
 * Case-insensitive by default
 *
 * @param accessor - Field accessor function
 * @param caseSensitive - Whether to use case-sensitive matching
 * @returns Exact match filter function
 */
export function createExactMatchFilter<T>(
  accessor: FieldAccessor<T>,
  caseSensitive = false
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    if (!query) return true;
    const value = accessor(item);
    if (!value) return false;

    if (caseSensitive) {
      return value === query;
    }

    return value.toLowerCase().trim() === query.toLowerCase().trim();
  };
}

/**
 * Creates a numeric range filter
 * Matches items where the field value is within the specified range
 *
 * @param accessor - Field accessor function that returns a number
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns Range filter function
 */
export function createRangeFilter<T>(
  accessor: (item: T) => number | null | undefined,
  min?: number,
  max?: number
): FilterFunction<T> {
  return (item: T, query: string): boolean => {
    const value = accessor(item);
    if (value == null) return false;

    const hasMin = min !== undefined;
    const hasMax = max !== undefined;

    if (hasMin && hasMax) {
      return value >= min && value <= max;
    } else if (hasMin) {
      return value >= min;
    } else if (hasMax) {
      return value <= max;
    }

    return true;
  };
}
